import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import { AccordionSection, CardIconStatus, Cell, HeaderCell, HeaderRow, Row, SortLabel, Status, StatusContainer, Table, TableBody, TableHead, Tooltip } from 'components';
import { theme } from 'design';
import { Appearance } from 'design/types';
import { EmployeeActionRequiredType, EmployeeStatus, EmployeeType, IEmployer, IEmployerSite } from 'types';
import { formatJobTitle } from 'utils/jobs';

interface IEmployersTableProps {
  employersForUser: Array<IEmployerSite | IEmployer>;
  isLoading: boolean;
}

const StyledAccordionSection = styled(AccordionSection)`
  z-index: ${theme.zIndex.contentHeader + 50};

  tr {
    text-transform: capitalize;
  }

  .employers-header {
    background-color: ${theme.color.neutral[100]};
  }
`;

const getStatusValues = (site?: IEmployerSite) => {
  if (!site) return { statusColor: Appearance.SUCCESS as Appearance, text: 'Approved' };

  switch (site.status) {
    case EmployeeStatus.APPROVED:
      return {
        statusColor: Appearance.SUCCESS,
        text: 'Approved',
      };
    case EmployeeStatus.INVITED:
    case EmployeeStatus.ACTION_REQUIRED:
      if (site.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
        return {
          statusColor: Appearance.WARNING,
          text: 'Pending Approval',
          pendingApproval: true,
        };
      }

      if (site.actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
        return {
          statusColor: Appearance.DANGER,
          text: 'Expired',
        };
      }
      return null;
    default:
      return {
        statusColor: Appearance.WARNING,
        text: 'Invited',
      };
  }
};

const sortBy = (emp: IEmployer | IEmployerSite, field: string) => {
  switch (field) {
    case 'role': {
      return (emp as IEmployer).type === EmployeeType.ADMIN ? 'org admin' : (emp as IEmployerSite).role;
    }
    case 'status': {
      const employerSite = (emp as IEmployer).sites ? (emp as IEmployer).sites[0] : (emp as IEmployerSite);
      const status = getStatusValues(employerSite);
      return status.text.toLowerCase();
    }
    default:
      return get(emp, field, '').toLowerCase();
  }
};

const warningIcon = <CardIconStatus color={Appearance.WARNING} icon="Caution" inverse />;

const EmployersTable: React.FC<IEmployersTableProps> = ({ employersForUser, isLoading }) => {
  const [sortOption, setSortOption] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'name', direction: 'asc' });

  // Don't show `dismissed` employer sites in the table
  const filteredEmployers = employersForUser.filter((employer) => {
    let emp: IEmployer | IEmployerSite = employer as IEmployerSite;

    if ((employer as IEmployer).type === EmployeeType.ADMIN) {
      emp = employer as IEmployer;
      return Object.values(emp.sites).length > 0 ? Object.values(emp.sites)[0].status !== EmployeeStatus.DISMISSED : [];
    }

    return emp.status !== EmployeeStatus.DISMISSED;
  });

  const sitesPendingApproval = filteredEmployers.some((emp) => {
    let e: IEmployer | IEmployerSite;

    if ((emp as IEmployer).type === EmployeeType.ADMIN) {
      e = emp as IEmployer;
      return Object.values(e.sites).length > 0 ? Object.values(e.sites)[0].actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL : undefined;
    }

    e = emp as IEmployerSite;
    return e.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL;
  });

  const sortedEmployers = orderBy(filteredEmployers, [(emp) => sortBy(emp, sortOption.field)], [sortOption.direction]);

  const isSortDesc = sortOption.direction === 'desc';
  const sortDirection = (field: string) => (sortOption.field === field && sortOption.direction === 'asc' ? 'desc' : 'asc');

  return (
    <StyledAccordionSection title="Employers" open={false} actions={sitesPendingApproval && warningIcon} dataCy="employers-accordion" className="employers">
      <Table appearance="noContainer">
        <TableHead>
          <HeaderRow sticky>
            <HeaderCell>
              <SortLabel label="Site" isSorted={sortOption.field === 'name'} isSortedDesc={isSortDesc} onClick={() => setSortOption({ field: 'name', direction: sortDirection('name') })} dataCy="site-sort-label" />
            </HeaderCell>
            <HeaderCell>
              <SortLabel label="Role" isSorted={sortOption.field === 'role'} isSortedDesc={isSortDesc} onClick={() => setSortOption({ field: 'role', direction: sortDirection('role') })} dataCy="role-sort-label" />
            </HeaderCell>
            <HeaderCell>
              <SortLabel label="Job Title" isSorted={sortOption.field === 'job.title'} isSortedDesc={isSortDesc} onClick={() => setSortOption({ field: 'job.title', direction: sortDirection('job.title') })} dataCy="job-sort-label" />
            </HeaderCell>
            <HeaderCell>
              <SortLabel
                label="Employee ID"
                isSorted={sortOption.field === 'employeeId'}
                isSortedDesc={isSortDesc}
                onClick={() => setSortOption({ field: 'employeeId', direction: sortDirection('employeeId') })}
                dataCy="employee-id-sort-label"
              />
            </HeaderCell>
            <HeaderCell>Disbursement</HeaderCell>
            <HeaderCell>
              <SortLabel label="Status" isSorted={sortOption.field === 'status'} isSortedDesc={isSortDesc} onClick={() => setSortOption({ field: 'status', direction: sortDirection('status') })} dataCy="status-sort-label" />
            </HeaderCell>
          </HeaderRow>
        </TableHead>

        <TableBody>
          {sortedEmployers.map((employer, index: number) => {
            let emp: IEmployer | IEmployerSite;

            if ((employer as IEmployer).type === EmployeeType.ADMIN) {
              emp = employer as IEmployer;

              const statusValues = getStatusValues(emp.sites[0]);
              return (
                <Row key={`employer-${index}`}>
                  <Cell>{isLoading ? <Skeleton width={55} /> : `${emp.name} - All`}</Cell>
                  <Cell>{isLoading ? <Skeleton width={55} /> : 'Org Admin'}</Cell>
                  <Cell>{isLoading ? <Skeleton width={55} /> : '--'}</Cell>
                  <Cell>{isLoading ? <Skeleton width={55} /> : '--'}</Cell>
                  <Cell>{isLoading ? <Skeleton width={55} /> : 'Instant'}</Cell>
                  <Cell>
                    {isLoading ? (
                      <Skeleton width={55} />
                    ) : (
                      <StatusContainer>
                        <Status data-tooltip-id={`pending-approval-${index}`} label={statusValues?.text || '--'} appearance={statusValues?.statusColor || Appearance.WARNING} />
                        {statusValues?.pendingApproval && (
                          <Tooltip id={`pending-approval-${index}`} place="top">
                            Notify your Site Admin to approve you
                          </Tooltip>
                        )}
                      </StatusContainer>
                    )}
                  </Cell>
                </Row>
              );
            }

            emp = employer as IEmployerSite;
            const statusValues = getStatusValues(emp);
            return (
              <Row key={`employer-${index}`}>
                <Cell>{isLoading ? <Skeleton width={55} /> : emp.name}</Cell>
                <Cell>{isLoading ? <Skeleton width={55} /> : emp.role}</Cell>
                <Cell>{isLoading ? <Skeleton width={55} /> : formatJobTitle(emp.job?.title || '--')}</Cell>
                <Cell>{isLoading ? <Skeleton width={55} /> : emp.employeeId || '--'}</Cell>
                <Cell>{isLoading ? <Skeleton width={55} /> : emp.disbursementMethod}</Cell>
                <Cell>
                  {isLoading ? (
                    <Skeleton width={55} />
                  ) : (
                    <StatusContainer>
                      <Status data-tooltip-id={`pending-approval-${index}`} label={statusValues?.text || '--'} appearance={statusValues?.statusColor || Appearance.WARNING} />
                      {statusValues?.pendingApproval && (
                        <Tooltip id={`pending-approval-${index}`} place="top">
                          Notify your Site Admin to approve you
                        </Tooltip>
                      )}
                    </StatusContainer>
                  )}
                </Cell>
              </Row>
            );
          })}
        </TableBody>
      </Table>
    </StyledAccordionSection>
  );
};

export default EmployersTable;
